import React from 'react';
import SEO from '../components/SEO';

export default function DataProtectionPage() {
  return (
    <div data-testid="data-protection-page">
      <SEO
        title="Data Protection & Privacy Policy"
        description="How Glossop & District Round Table 916 protects and manages your personal information in compliance with GDPR and the Data Protection Act 2018."
        path="/policies/data-protection"
        keywords={['data protection', 'privacy policy', 'GDPR', 'Glossop Round Table privacy']}
      />

      <div className="pt-28 sm:pt-32 pb-20 sm:pb-28 bg-white min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">Privacy & Data Protection</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-8">
            Data Protection & Privacy Policy
          </h1>
          <p className="text-base text-[#52525B] leading-relaxed mb-6 italic">
            How we protect and manage your personal information
          </p>

          <div className="space-y-8 text-base text-[#52525B] leading-relaxed">
            <p>
              We are committed to protecting your right to privacy and to ensuring that your information is secure. To prevent unauthorised access or disclosure we have put in place suitable physical, digital and managerial procedures to safeguard and secure the information we collect.
            </p>
            <p>
              We will always adhere to the General Data Protection Act 2018, and the General Data Protection Regulation (EU) 2016/679 ("GDPR"), and any other applicable data protection laws in relation to our use of your information.
            </p>

            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">Information that we collect</h2>
              <p className="mb-4">
                We collect information about users to continually help improve our service. To operate effectively and fulfil its legal obligations, our organisation needs to collect, maintain and use certain personal information about current, past and prospective members, supporters and other individuals with whom it has dealings.
              </p>
              <p className="mb-4">
                We are committed to the eight principles of data protection as detailed in the General Data Protection Act 2018. These principles require that personal information must:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be fairly and lawfully processed and not processed unless specific conditions are met</li>
                <li>Be obtained for one or more specified, lawful purposes and not processed in any manner incompatible with those purposes</li>
                <li>Be adequate, relevant and not excessive for those purposes</li>
                <li>Be accurate and, where necessary, kept up to date</li>
                <li>Not be kept for longer than is necessary</li>
                <li>Be processed in accordance with the data subject's rights under the DPA</li>
                <li>Be kept secure from unauthorised or unlawful processing and protected against accidental loss, destruction or damage</li>
                <li>Not be transferred to countries outside the European Economic Area, unless the country or territory ensures adequate protection</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">How we use information</h2>
              <p className="mb-4">
                We may collect and process the information we collect from you to fulfil your web page requests, contact you, or conduct research. When you contact us, we keep a record of your communication to help solve any issues you might be facing.
              </p>
              <p>
                Our website and online services use cookies. We use information collected from cookies to improve your user experience and the overall quality of our services.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">Protecting your personal information</h2>
              <p className="mb-4">
                To the extent that sensitive personal data is processed about you, we will employ appropriate security measures given the nature of that data. We only provide certain individuals with access to personal data and ensure that these individuals are contracted to do so under relevant data protection policies.
              </p>
              <p>
                Breach of these policies are regarded as a disciplinary offence and will be dealt with under formal procedures.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">Marketing emails &amp; the Bonfire Night launch list</h2>
              <p className="mb-4">
                When you sign up to our Bonfire Night launch list, we collect your name and email address solely so that
                we can notify you when tickets go on sale. Our lawful basis for processing this data is your{' '}
                <strong>explicit consent</strong> (UK GDPR Article 6(1)(a)) given when you tick the consent box on the
                sign-up form. We comply with the Privacy and Electronic Communications Regulations 2003 (PECR).
              </p>
              <p className="mb-4">
                We will never share, sell or pass your details to any third party. Your data is held only for as long
                as is necessary to fulfil the purpose of the launch list, and is deleted once that purpose has ended.
              </p>
              <p>
                You can <strong>unsubscribe at any time</strong> by clicking the one-click unsubscribe link included
                at the bottom of every marketing email we send, or by emailing{' '}
                <a href="mailto:membership@glossoproundtable.org" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">
                  membership@glossoproundtable.org
                </a>. Once unsubscribed, we will not contact you again about this list.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">Your Rights</h2>
              <p>
                There are several rights that you may exercise which will depend on the legal basis for processing your personal data. You can at any time request a copy of the personal information that we hold about you, or have your personal data corrected or erased.
              </p>
            </div>

            <div className="p-6 bg-[#F4F4F5] border-l-4 border-[#F0A500]">
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">Contact Us</h3>
              <p>
                If you have any concerns about how we process your personal data, or wish to discuss or exercise your rights, please contact us at{' '}
                <a href="mailto:info@glossoproundtable.org" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">
                  info@glossoproundtable.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
